import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type PowensStatus = "disconnected" | "connecting" | "connected" | "syncing" | "error";

type SyncStatus = "idle" | "syncing" | "no_documents" | "complete" | "error";

interface PowensConnection {
  id: string;
  powens_connection_id: string;
  connector_name: string | null;
  state: string | null;
  last_sync_at: string | null;
}

interface SyncResult {
  status: string;
  imported?: number;
  skipped?: number;
  total?: number;
  subscriptions?: number;
  message?: string;
  smartMessage?: string;
  importedDocuments?: Array<{ title: string; issuer: string | null; type: string }>;
  errors?: string[];
}

export function useEasySyncConnection() {
  const [status, setStatus] = useState<PowensStatus>("disconnected");
  const [connections, setConnections] = useState<PowensConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const syncTriggeredRef = useRef(false);

  // Check connection status on mount
  useEffect(() => {
    checkConnection();
  }, []);

  // Check URL params for callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const powensResult = params.get("powens");
    const syncPending = params.get("sync_pending");
    
    if (powensResult === "success") {
      toast.success("Documents officiels connectés avec succès");
      setStatus("connected");
      checkConnection();
      
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      
      // Auto-trigger sync if pending and not already triggered
      if (syncPending === "true" && !syncTriggeredRef.current) {
        syncTriggeredRef.current = true;
        // Small delay to ensure state is updated
        setTimeout(() => {
          sync();
        }, 1000);
      }
    } else if (powensResult === "error") {
      const message = params.get("message") || "Erreur de connexion";
      toast.error(`Erreur de connexion: ${message}`);
      setStatus("error");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const checkConnection = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Check powens_tokens table
      const { data: tokens, error } = await supabase
        .from("powens_tokens")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error checking Powens connection:", error);
        setStatus("disconnected");
        setLoading(false);
        return;
      }

      if (tokens) {
        setStatus("connected");
        
        // Fetch connections
        const { data: conns } = await supabase
          .from("powens_connections")
          .select("*")
          .eq("user_id", user.id);
        
        if (conns) {
          setConnections(conns);
        }
      } else {
        setStatus("disconnected");
      }
    } catch (err) {
      console.error("Powens check error:", err);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  }, []);

  const connect = useCallback(async () => {
    setStatus("connecting");
    
    try {
      const { data, error } = await supabase.functions.invoke("powens-auth");
      
      if (error) {
        console.error("Powens auth error:", error);
        toast.error("Erreur lors de l'initialisation de la connexion");
        setStatus("error");
        return;
      }

      if (data?.url) {
        // Redirect to Powens webview
        window.location.href = data.url;
      } else {
        toast.error("URL de connexion non reçue");
        setStatus("error");
      }
    } catch (err) {
      console.error("Powens connect error:", err);
      toast.error("Erreur de connexion aux organismes");
      setStatus("error");
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Delete tokens and connections
      await supabase.from("powens_tokens").delete().eq("user_id", user.id);
      await supabase.from("powens_connections").delete().eq("user_id", user.id);
      await supabase.from("connected_accounts").delete()
        .eq("user_id", user.id)
        .eq("provider", "powens");

      setStatus("disconnected");
      setConnections([]);
      setSyncStatus("idle");
      setLastSyncResult(null);
      toast.success("Connexion aux organismes désactivée");
    } catch (err) {
      console.error("Powens disconnect error:", err);
      toast.error("Erreur lors de la déconnexion");
    }
  }, []);

  const sync = useCallback(async () => {
    setSyncing(true);
    setSyncStatus("syncing");
    setStatus("syncing");
    
    try {
      const { data, error } = await supabase.functions.invoke("powens-sync");
      
      if (error) {
        console.error("Powens sync error:", error);
        toast.error("Erreur de synchronisation");
        setSyncStatus("error");
        setStatus("connected");
        return;
      }

      const result = data as SyncResult;
      setLastSyncResult(result);

      if (result.status === "syncing") {
        // Connections are still syncing on Powens side
        toast.info(result.message || "Synchronisation en cours côté banque...");
        setSyncStatus("syncing");
        
        // Retry after a delay
        setTimeout(() => {
          sync();
        }, 5000);
        return;
      }

      if (result.status === "no_documents") {
        toast.info(result.message || "Aucun document disponible pour ce connecteur");
        setSyncStatus("no_documents");
      } else if (result.status === "complete") {
        const imported = result.imported || 0;
        const total = result.total || 0;
        
        if (imported > 0) {
          // Utiliser le message intelligent s'il existe
          const message = result.smartMessage || 
            `${imported} document${imported > 1 ? 's' : ''} importé${imported > 1 ? 's' : ''}`;
          toast.success(message);
        } else if (total > 0) {
          toast.info("Tous les documents étaient déjà importés");
        } else {
          toast.info("Aucun nouveau document trouvé");
        }
        setSyncStatus("complete");
      } else if (result.status === "error") {
        toast.error("Erreur lors de la synchronisation");
        setSyncStatus("error");
      }

      setStatus("connected");
      await checkConnection();
    } catch (err) {
      console.error("Powens sync error:", err);
      toast.error("Erreur de synchronisation");
      setSyncStatus("error");
      setStatus("connected");
    } finally {
      setSyncing(false);
    }
  }, [checkConnection]);

  return {
    status,
    connections,
    loading,
    syncing,
    syncStatus,
    lastSyncResult,
    connect,
    disconnect,
    sync,
    checkConnection,
  };
}
